import React, { useMemo } from 'react';
import { HashLink as Link } from 'react-router-hash-link';
import { get } from 'lodash';
import { ensureUser } from '../util/data';
import { formatResponseTime } from '../util/responseTime';
import { useUserReviews } from './queries/useUserReviews';

/**
 * Custom hook to prepare data for LandownerProfile component
 * @param {Object} params
 * @param {Object} params.listing - The listing object with author information
 * @param {boolean} params.showRate - Whether to show response rate and time
 * @param {Object} params.intl - The intl object for internationalization
 * @returns {Object} - Object containing all data needed for LandownerProfile component
 */
const useHostProfile = ({ listing, showRate = false, intl }) => {
  // Use React Query hook for reviews with automatic caching
  const { data: reviewsData, isLoading: isFetchingReviews } = useUserReviews({
    userId: listing?.author?.id?.uuid,
  });

  const reviews = reviewsData?.reviews;
  const reviewsRatingAvg = reviewsData?.reviewsRatingAvg || 0;
  const hostData = useMemo(() => {
    if (!listing?.author) {
      return null;
    }

    const ensuredAuthor = ensureUser(listing.author);
    const { displayName, bio, metadata } = ensuredAuthor.attributes.profile;
    const { createdAt } = ensuredAuthor.attributes;

    // Get hostingSince as ISO date string
    let hostingSince = null;
    if (createdAt) {
      if (createdAt instanceof Date) {
        hostingSince = createdAt.toISOString();
      } else {
        const createdAtDate = new Date(createdAt);
        if (!Number.isNaN(createdAtDate.getTime())) {
          hostingSince = createdAtDate.toISOString();
        }
      }
    }

    // Get avatar URL
    const avatarSrc =
      ensuredAuthor.profileImage?.attributes?.variants?.['square-small2x']?.url || null;

    // Get verification status
    const isVerified = get(ensuredAuthor, 'attributes.profile.publicData.hasStripe', false);

    // Get first name for message button
    const firstName = displayName ? displayName.split(' ')[0] : 'Landowner';

    // Get response rate and time
    const rawRate = get(metadata, 'allTimeStats.responseRate', null);
    const responseRate = rawRate ? `${parseInt(rawRate, 10)}%` : null;

    const rawTime = get(metadata, 'metrics.responseTime.last30DaysAverageHours', null);
    const responseTime = rawTime ? formatResponseTime(rawTime) : null;

    // Generate reviews link
    const reviewsLink = ensuredAuthor.id ? `/u/${ensuredAuthor.id.uuid}#reviews` : '#';

    return {
      displayName,
      bio,
      hostingSince,
      avatarSrc,
      isVerified,
      firstName,
      responseRate: showRate && responseRate,
      responseTime: showRate ? responseTime : null,
      reviewsLink,
    };
  }, [listing, showRate]);

  const featureReviewItem = useMemo(() => {
    if (!hostData) return null;

    const totalReviews = reviews?.length || 0;
    const isReviewed = totalReviews > 0;

    if (isReviewed && !isFetchingReviews) {
      return {
        iconVariant: 'StarSolid',
        iconColor: 'var(--icon-warning)',
        label: (
          <span>
            {reviewsRatingAvg} •{' '}
            <Link to={hostData.reviewsLink} style={{ textDecoration: 'underline' }}>
              {intl.formatMessage({ id: 'UserCard.reviews' }, { count: totalReviews })}
            </Link>
          </span>
        ),
        labelSize: 'sm',
      };
    }

    if (isFetchingReviews) {
      return {
        iconVariant: 'StarSolid',
        iconColor: 'var(--icon-warning)',
        label: 'Loading reviews...',
        isLoading: true,
      };
    }

    return null;
  }, [hostData, reviews, reviewsRatingAvg, isFetchingReviews, intl]);

  return {
    hostData,
    featureReviewItem,
  };
};

export default useHostProfile;
